import { Icon } from '@studio/design-system/icon';
import type { KeyboardEvent, ReactElement } from 'react';
import { useEffect, useId, useRef } from 'react';
import { Sel, STagNew } from '#~/stag/index.js';

export interface ReplacementModalProps {
  readonly machineNames: readonly string[];
  readonly busy: boolean;
  readonly onCancel: (reason: 'cancel' | 'close' | 'backdrop' | 'escape') => void;
  readonly onConfirm: () => void;
}

/** Confirm the exact Bridge set that an atomic replacement will displace. */
export const ReplacementModal = ({
  machineNames,
  busy,
  onCancel,
  onConfirm
}: ReplacementModalProps): ReactElement => {
  const modalRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const idSeed = useId().replaceAll(':', '');
  const titleId = `unlink-bridge-title-${idSeed}`;
  const descriptionId = `unlink-bridge-description-${idSeed}`;
  const displayedNames = machineNames.length > 0 ? machineNames : ['the existing Bridge'];

  useEffect(() => {
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    modalRef.current?.focus();
    return () => returnFocusRef.current?.focus();
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      if (!busy) onCancel('escape');
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...(modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [])
    ];
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable.at(-1)!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-dark/[0.72] p-24 backdrop-blur-[3px]'
      {...STagNew(Sel.StudioPicker, 'replacement_backdrop')}
      onClick={(event): void => {
        if (event.target === event.currentTarget && !busy) onCancel('backdrop');
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={modalRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy}
        tabIndex={-1}
        // See knowledge: design-system-tailwind-color-tokens (ai.knowledge/studio.client) — unsupported named values emit no CSS.
        className='relative w-full max-w-[460px] rounded-[16px] border border-white/[0.18] bg-base-200 px-32 pb-[26px] pt-[34px] text-center text-light shadow-[0_20px_56px_rgba(0,0,0,0.65)] focus:outline-none'
        {...STagNew(Sel.StudioPicker, 'replacement_modal')}
      >
        <button
          type='button'
          aria-label='Cancel'
          disabled={busy}
          className='absolute right-[14px] top-[14px] flex size-32 items-center justify-center rounded-6 text-muted hover:bg-base-50 hover:text-light focus-visible:outline-none focus-visible:ring disabled:opacity-50'
          {...STagNew(Sel.StudioPicker, 'replacement_close')}
          onClick={(): void => onCancel('close')}
        >
          <svg aria-hidden='true' width='16' height='16' viewBox='0 0 16 16' fill='none'>
            <path
              d='M4 4l8 8M12 4l-8 8'
              stroke='currentColor'
              strokeWidth='1.5'
              strokeLinecap='round'
            />
          </svg>
        </button>

        {/* Match the destructive-action chip to the existing Bridge glyph. */}
        <span
          aria-hidden='true'
          data-testid='unlink-bridge-icon'
          className='mx-auto mb-[14px] flex size-40 items-center justify-center rounded-full bg-error/[0.14] text-error'
        >
          <Icon icon='bridge' className='text-[20px]' />
        </span>
        <h2 id={titleId} className='text-[24px] font-demibold leading-[1.2]'>
          Unlink Bridge
        </h2>
        <p
          id={descriptionId}
          className='mt-14 mx-auto max-w-[360px] text-[15px] leading-[1.55] text-muted'
        >
          A Studio can only be linked to one Bridge at a time. To link this Studio to a new Bridge,
          you must first unlink{' '}
          {displayedNames.map((name, index) => (
            <span key={`${name}-${index}`}>
              {index > 0 && (index === displayedNames.length - 1 ? ' and ' : ', ')}
              <strong className='font-demibold text-light'>{name}</strong>
            </span>
          ))}
          .
        </p>

        <div className='mt-28 flex justify-center gap-12'>
          <button
            type='button'
            disabled={busy}
            className='h-[46px] rounded-6 border border-white/25 px-24 text-[13px] font-demibold text-light hover:bg-base-50 focus-visible:outline-none focus-visible:ring disabled:opacity-50'
            {...STagNew(Sel.StudioPicker, 'replacement_cancel')}
            onClick={(): void => onCancel('cancel')}
          >
            Cancel
          </button>
          {/* Unlinking is destructive, so the confirm button takes the design
              system's error token (--iris-error), not the warning token that
              reads as "heads up" (ENG-4483 ITB finding #006). */}
          <button
            type='button'
            disabled={busy}
            className='h-[46px] rounded-6 bg-error px-24 text-[13px] font-demibold text-white hover:bg-error-highlight focus-visible:outline-none focus-visible:ring disabled:opacity-50'
            {...STagNew(Sel.StudioPicker, 'replacement_confirm')}
            onClick={onConfirm}
          >
            Unlink Bridge
          </button>
        </div>
      </div>
    </div>
  );
};
